import React from 'react';
import { motion, easeInOut, easeOut } from 'framer-motion';
import { UserX, Search, AlertCircle, RefreshCw } from 'lucide-react';

interface UserNotFoundCardProps {
  message: string;
  onRetry: () => void;
}

export const UserNotFoundCard: React.FC<UserNotFoundCardProps> = ({ message, onRetry }) => {
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: easeOut
      }
    }
  };

  const floatingVariants = {
    float: {
      y: [-10, 10, -10],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: easeInOut
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="max-w-lg mx-auto"
    >
      <div className="bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200 rounded-2xl p-8 shadow-2xl text-center">
        <motion.div
          variants={floatingVariants}
          animate="float"
          className="flex items-center justify-center mb-6"
        >
          <div className="relative">
            <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center">
              <UserX className="w-10 h-10 text-white" />
            </div>
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 300 }}
              className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center"
            >
              <AlertCircle className="w-5 h-5 text-white" />
            </motion.div>
          </div>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-3xl font-bold text-gray-800 mb-4"
        >
          User Not Found
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-gray-600 mb-6 leading-relaxed"
        >
          {message}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
        >
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Search className="w-6 h-6 text-orange-500" />
            <h3 className="text-lg font-semibold text-gray-800">Search Tips</h3>
          </div>

          <div className="text-left space-y-2 text-sm text-gray-600">
            <p>• Try searching by username (e.g., "johndoe")</p>
            <p>• Try searching by email address</p>
            <p>• Try searching by user ID</p>
            <p>• Check for typos in your search</p>
          </div>
        </motion.div>



        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          onClick={onRetry}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white font-medium rounded-full shadow-lg hover:shadow-xl transition-all duration-200"
        >
          <RefreshCw className="w-5 h-5 mr-2" />
          Try Again
        </motion.button>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-6 text-xs text-gray-500"
        >
          Make sure the user identifier is correct and try again.
        </motion.div>
      </div>
    </motion.div>
  );
};